import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import type { ArtikelTyp } from '../lib/api-enums';
import { ARTIKEL_TYP_LABELS as TYP_LABELS } from '../lib/api-labels';
import { UST_SATZ_DEFAULT } from '../lib/steuer-konstanten';

interface Artikel {
  id: string;
  artikelnummer: string | null;
  bezeichnung: string;
  beschreibung: string | null;
  typ: ArtikelTyp;
  einheit: string;
  preisNetto: string;
  ustSatz: string;
  aktiv: boolean;
  notizen: string | null;
}

export const Route = createFileRoute('/artikel/')({
  beforeLoad: () => requireAccess('/api/v1/artikel'),
  component: ArtikelPage,
});

function ArtikelPage() {
  const qc = useQueryClient();
  const [typFilter, setTypFilter] = useState<'' | ArtikelTyp>('');
  const [aktivFilter, setAktivFilter] = useState<'' | 'true' | 'false'>('true');

  const listKey = ['artikel', typFilter, aktivFilter] as const;
  const list = useQuery({
    queryKey: listKey,
    queryFn: () => {
      const qs = new URLSearchParams();
      if (typFilter) qs.set('typ', typFilter);
      if (aktivFilter) qs.set('aktiv', aktivFilter);
      const path = `/api/v1/artikel${qs.toString() ? '?' + qs.toString() : ''}`;
      return api<{ items: Artikel[] }>(path);
    },
  });

  const [showForm, setShowForm] = useState(false);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Artikel & Leistungen"
        description="Wiederverwendbare Positionen für Angebote und Rechnungen. aktiv=false sperrt den Artikel, ohne Historie zu verlieren."
        actions={
          <Button onClick={() => setShowForm((s) => !s)} variant={showForm ? 'ghost' : 'primary'}>
            {showForm ? 'Abbrechen' : '+ Neuer Artikel'}
          </Button>
        }
      />

      <div className="flex items-center gap-3 text-sm">
        <label>
          <span className="text-slate-600 mr-2">Typ</span>
          <Select
            value={typFilter}
            onChange={(e) => setTypFilter(e.target.value as ArtikelTyp | '')}
            className="w-48 inline-block"
          >
            <option value="">alle</option>
            {(Object.keys(TYP_LABELS) as ArtikelTyp[]).map((t) => (
              <option key={t} value={t}>
                {TYP_LABELS[t]}
              </option>
            ))}
          </Select>
        </label>
        <label>
          <span className="text-slate-600 mr-2">Status</span>
          <Select
            value={aktivFilter}
            onChange={(e) => setAktivFilter(e.target.value as 'true' | 'false' | '')}
            className="w-40 inline-block"
          >
            <option value="true">aktiv</option>
            <option value="false">inaktiv</option>
            <option value="">alle</option>
          </Select>
        </label>
      </div>

      {showForm && (
        <ArtikelCreateForm
          onDone={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ['artikel'] });
          }}
        />
      )}

      <ErrorBanner error={list.error} />

      {list.data && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left w-28">Nr.</th>
                <th className="px-4 py-2 text-left">Bezeichnung</th>
                <th className="px-4 py-2 text-left w-36">Typ</th>
                <th className="px-4 py-2 text-left w-24">Einheit</th>
                <th className="px-4 py-2 text-right w-28">Netto</th>
                <th className="px-4 py-2 text-right w-20">USt</th>
                <th className="px-4 py-2 text-center w-20">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.data.items.map((a) => (
                <ArtikelRow key={a.id} artikel={a} />
              ))}
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                    Keine Artikel zu diesem Filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ArtikelCreateForm({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState({
    artikelnummer: '',
    bezeichnung: '',
    typ: 'pauschal' as ArtikelTyp,
    einheit: 'Std',
    preisNetto: '',
    ustSatz: UST_SATZ_DEFAULT,
  });
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        bezeichnung: state.bezeichnung,
        typ: state.typ,
        einheit: state.einheit,
        preisNetto: Number(state.preisNetto),
        ustSatz: Number(state.ustSatz),
      };
      if (state.artikelnummer.trim()) body['artikelnummer'] = state.artikelnummer.trim();
      return api<{ item: Artikel }>('/api/v1/artikel', { method: 'POST', body });
    },
    onSuccess: () => onDone(),
    onError: (err) => setError(err),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 shadow-sm"
    >
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Nummer</label>
          <Input
            value={state.artikelnummer}
            onChange={(e) => setState({ ...state, artikelnummer: e.target.value })}
            placeholder="A-001"
          />
        </div>
        <div className="md:col-span-3">
          <label className="text-xs font-medium text-slate-600">Bezeichnung</label>
          <Input
            required
            value={state.bezeichnung}
            onChange={(e) => setState({ ...state, bezeichnung: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Typ</label>
          <Select
            value={state.typ}
            onChange={(e) => setState({ ...state, typ: e.target.value as ArtikelTyp })}
          >
            {(Object.keys(TYP_LABELS) as ArtikelTyp[]).map((t) => (
              <option key={t} value={t}>
                {TYP_LABELS[t]}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Einheit</label>
          <Input
            required
            value={state.einheit}
            onChange={(e) => setState({ ...state, einheit: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Preis netto (EUR)</label>
          <Input
            required
            type="number"
            step="0.01"
            min="0"
            value={state.preisNetto}
            onChange={(e) => setState({ ...state, preisNetto: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">USt-Satz (%)</label>
          <Input
            required
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={state.ustSatz}
            onChange={(e) => setState({ ...state, ustSatz: e.target.value })}
          />
        </div>
      </div>
      <ErrorBanner error={error} />
      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Speichere…' : 'Artikel anlegen'}
        </Button>
      </div>
    </form>
  );
}

function ArtikelRow({ artikel }: { artikel: Artikel }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [bezeichnung, setBezeichnung] = useState(artikel.bezeichnung);
  const [preisNetto, setPreisNetto] = useState(artikel.preisNetto);
  const [aktiv, setAktiv] = useState(artikel.aktiv);
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, unknown> = {};
      if (bezeichnung !== artikel.bezeichnung) patch['bezeichnung'] = bezeichnung;
      if (preisNetto !== artikel.preisNetto) patch['preisNetto'] = Number(preisNetto);
      if (aktiv !== artikel.aktiv) patch['aktiv'] = aktiv;
      if (Object.keys(patch).length === 0) return artikel;
      return api<{ item: Artikel }>(`/api/v1/artikel/${artikel.id}`, {
        method: 'PATCH',
        body: patch,
      });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['artikel'] });
    },
    onError: (err) => setError(err),
  });

  if (!editing) {
    return (
      <tr className="text-sm text-slate-800">
        <td className="px-4 py-2 font-mono text-xs text-slate-600">
          {artikel.artikelnummer ?? '–'}
        </td>
        <td className="px-4 py-2 font-medium">
          <Link
            to="/artikel/$id"
            params={{ id: artikel.id }}
            className="text-brand-700 hover:underline"
          >
            {artikel.bezeichnung}
          </Link>
        </td>
        <td className="px-4 py-2">{TYP_LABELS[artikel.typ]}</td>
        <td className="px-4 py-2 text-slate-600">{artikel.einheit}</td>
        <td className="px-4 py-2 text-right tabular-nums">
          {Number(artikel.preisNetto).toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{' '}
          €
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-slate-600">{artikel.ustSatz} %</td>
        <td className="px-4 py-2 text-center">
          <span
            className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${
              artikel.aktiv
                ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                : 'bg-slate-100 text-slate-500 border-slate-200'
            }`}
          >
            {artikel.aktiv ? 'aktiv' : 'inaktiv'}
          </span>
        </td>
        <td className="px-4 py-2 text-right">
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Bearbeiten
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="text-sm bg-brand-50/50">
      <td className="px-4 py-2 font-mono text-xs text-slate-600">{artikel.artikelnummer ?? '–'}</td>
      <td className="px-4 py-2">
        <Input value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} />
      </td>
      <td className="px-4 py-2">{TYP_LABELS[artikel.typ]}</td>
      <td className="px-4 py-2 text-slate-600">{artikel.einheit}</td>
      <td className="px-4 py-2">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={preisNetto}
          onChange={(e) => setPreisNetto(e.target.value)}
          className="text-right tabular-nums"
        />
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-slate-600">{artikel.ustSatz} %</td>
      <td className="px-4 py-2 text-center">
        <label className="inline-flex items-center gap-1 text-xs">
          <input type="checkbox" checked={aktiv} onChange={(e) => setAktiv(e.target.checked)} />
          aktiv
        </label>
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-2">
          {error instanceof Error && <span className="text-xs text-red-600">{error.message}</span>}
          <Button variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Speichere…' : 'Speichern'}
          </Button>
        </div>
      </td>
    </tr>
  );
}
